import Constants from '@/Constants';
import { BotRecommendationRequest } from '@/models/BotRecommendationRequest';
import { DialogFlowMessage, DialogflowMessageType } from '@/models/Chat';
import { BufferingMessages } from '@/pages/Chat/ChatModal/types';
import { Reducer } from 'redux';
import { PURGE } from 'redux-persist';
import { LearningMap } from '../learningMap/types';
import { AnsweredMessage, ChatActionTypes, ChatMessage, ChatState } from './types';

const INITIAL_STATE: ChatState = {
    data: {
        eventMessages: [],
        messages: [],
        buffering: [0, 1],
    },
    buildingLearningMap: {
        emailSuggestions: false,
        suggestionTimeOfTheDay: Constants.periodOfDay.AFTERNOON,
    } as LearningMap,
    buildingContentRecommendations: {} as BotRecommendationRequest,
    categoriesList: [],
    loading: false,
    hasError: false,
    openedChat: false,
    hasUnreadMessages: false,
    action: null,
};

const createUserMessage = (message: any): DialogFlowMessage => ({
    ...message,
    messageType: DialogflowMessageType.USER,
    payload: null,
});

type MergeMessageStack<T> = (messages: T[], replaceLast?: boolean) => T[];

const reducer: Reducer<ChatState> = (state: ChatState = INITIAL_STATE, action) => {
    const getMessageByIndex = (index: number) => state.data.messages[index];

    const mergeMessages: MergeMessageStack<DialogFlowMessage> = (messages, replaceLast) => {
        const currentMesssages = state.data.messages;

        if (replaceLast) {
            currentMesssages.pop();
        }

        return currentMesssages.concat(messages);
    };

    const {
        buffering: [total, progress],
    } = state.data;

    switch (action.type) {
        case PURGE:
            return { ...INITIAL_STATE };

        case ChatActionTypes.COMPLETE_SEND_CHAT_MESSAGE_REQUEST: {
            const { isAnswer, ...userMessage } = action.payload as ChatMessage;
            const message: DialogFlowMessage[] = isAnswer ? [] : [createUserMessage(userMessage)];

            if (isAnswer) {
                const lastMessage: DialogFlowMessage = state.data.messages[state.data.messages.length - 1];
                lastMessage.messageType = DialogflowMessageType.USER;
                lastMessage.message = userMessage.message;
            }

            const messages = mergeMessages(message);

            return { ...state, loading: true, hasError: false, data: { ...state.data, messages } };
        }

        case ChatActionTypes.COMPLETE_CHAT_SUCCESS: {
            const {
                data: { chatbotMessagesData },
                replaceLastMessage,
            } = action.payload;

            const messages = mergeMessages(chatbotMessagesData, replaceLastMessage);

            const buffering: BufferingMessages = [total + chatbotMessagesData.length, progress];
            return { ...state, loading: false, hasError: false, hasUnreadMessages: false, data: { buffering, messages, eventMessages: [] } };
        }

        case ChatActionTypes.BUFFERING: {
            const buffering: BufferingMessages = [total, progress + 1];
            return { ...state, loading: false, hasError: false, data: { ...state.data, buffering } };
        }

        case ChatActionTypes.MARK_CHIP_ANSWER: {
            const message = getMessageByIndex(action.payload.index) as AnsweredMessage;
            message.answer = { options: action.payload.options };
            return { ...state, data: { ...state.data } };
        }

        case ChatActionTypes.BUILDING_LEARNING_MAP:
            return { ...state, buildingLearningMap: { ...state.buildingLearningMap, ...action.payload } };

        case ChatActionTypes.BUILDING_CONTENT_RECOMMENDATIONS:
            return { ...state, buildingContentRecommendations: { ...state.buildingContentRecommendations, ...action.payload } };
        case ChatActionTypes.BUILDING_CONTENT_RECOMMENDATIONS_CLEAR:
            return { ...state, buildingContentRecommendations: {} as BotRecommendationRequest };

        case ChatActionTypes.GET_RECOMMENDATION_REQUEST:
            return { ...state, loading: true, hasError: false };

        case ChatActionTypes.SEND_NEW_EVENT_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ChatActionTypes.SEND_NEW_EVENT_SUCCESS:
            return {
                ...state,
                loading: false,
                hasError: false,
                hasUnreadMessages: false,
                data: { ...state.data, eventMessages: action.payload.chatbotMessagesData },
            };
        case ChatActionTypes.SEND_NEW_EVENT_FAILURE:
            return { ...state, hasError: true, loading: false };

        case ChatActionTypes.COMPLETE_CHAT_FAILURE:
            return { ...state, hasError: true, loading: false };

        case ChatActionTypes.GET_CATEGORIES_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ChatActionTypes.GET_CATEGORIES_SUCCESS:
            return { ...state, loading: false, categoriesList: action.payload };
        case ChatActionTypes.GET_CATEGORIES_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ChatActionTypes.GET_CATEGORIES_CLEAR:
            return { ...state, categoriesList: [] };

        case ChatActionTypes.SELECT_RECOMMENDATION_CONTENT_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ChatActionTypes.SELECT_RECOMMENDATION_CONTENT_SUCCESS:
            return { ...state, loading: false, hasError: false };
        case ChatActionTypes.SELECT_RECOMMENDATION_CONTENT_FAILURE:
            return { ...state, loading: false, hasError: true };

        case ChatActionTypes.OPEN_CHAT_MODAL:
            return { ...state, openedChat: true };
        case ChatActionTypes.CLOSE_CHAT_MODAL:
            return { ...state, openedChat: false };

        case ChatActionTypes.SET_UNREAD_MESSAGES:
            return { ...state, hasUnreadMessages: true };

        case ChatActionTypes.SET_BOT_ACTION:
            return { ...state, action: action.payload };

        case ChatActionTypes.DISMISS_BOT_ACTION:
            return { ...state, action: null };

        default:
            return state;
    }
};

export default reducer;
